import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { autobind } from 'core-decorators'
import { confirmable } from 'react-confirm'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import { observable, action } from 'mobx'
import { formatMessage, FormattedMessage } from '../../../translations'
import { Dialog } from '../../../shared/components/Dialog'
import { store as imageStore } from '../..'
import { i18n } from '../../../shared/utils'
import cacheImage from '../../cacheImage'
import ImagePropagationLanguageEntry from './ImagePropagationLanguageEntry'
import GenevaButton from '../../../ui/components/GenevaButton'

const css = /* typeof window === 'undefined' ? {} : */require('../../styles.scss')

/**
  * Renders two versions of the dialog.  One if the image is switched,
  * then it uses a cached image to show before and after.
  * The second is if there was no image and then it is being added.  Since the
  * image was deleted or never there, we don't have the connection to the other
  * images and need to show only a basic dialog
  */
@confirmable
@observer
class ImagePropagation extends Component {

  static propTypes = {
    show: PropTypes.bool,            // indicates if the dialog is shown or not.
    proceed: PropTypes.func,         // call to close the dialog with promise resolved.
    cancel: PropTypes.func,          // call to close the dialog with promise rejected.
    dismiss: PropTypes.func,         // call to only close the dialog.
    confirmation: PropTypes.string,  // arguments of your confirm function
    options: PropTypes.shape({
      langList: PropTypes.object,
      currentLang: PropTypes.string,
      cachedImage: PropTypes.object,
      channel: PropTypes.string,
      hideLangVariantList: PropTypes.array,
    }),
    handler: PropTypes.object,        // Set of handler functions
  }

  constructor(props) {
    super(props)

    this.completeLanguageSet = this.props.options.langList
      .map(lang => lang.createdIso)
      .filter(entry => entry !== props.options.currentLang)
      .filter(entry => !props.options.hideLangVariantList.find(el => el === entry))

    this.disabledSet = new Set() // will be filled out in render functions

    // specificed cache id per load of IP, so that images are not forced loaded for every re-render
    this.instanceCacheBust = new Date().getTime()

    this.currentImage = this.props.options.savedImage

    if (!this.currentImage) {
      console.warn('ImagePropagation: No current image provided, dialog will not be able to show image previews')

      imageStore.load(this.props.options.currImage.id)
        .then((model) => {
          this.currentImage = model
        })
        .catch((err) => {
          console.warn(`Could not load image for propagation dialog: ${err.message}`)
        })
    }
  }

  /** eslint-disable react/sort-comp */
  /** Actions on the component state */
  @action setLangGroupSelect(group) {
    this.langSetSelected = group
  }

  /* local state by observables */
  @observable langSetSelected = []

  @observable currentImage
  /** eslint-enable react/sort-comp */


  getCurrentImageForLanguage(lang = this.props.options.currentLang) {
    // this will either create a new image if the src can be established or just
    // be null
    return cacheImage({
      src: this.currentImage && i18n(
        this.currentImage,
        `draft.channels.${this.props.options.channel}.url`,
        lang
      )
    }, this.instanceCacheBust)

  }

  @autobind
  handleCommit(e) {
    e.preventDefault()
    e.stopPropagation()

    // if isRemove, add currentLang, as that is always considered selected and not yet acted on
    if (this.props.options.isRemoval) {
      this.langSetSelected.push(this.props.options.currentLang)
    }

    // If nothing selected, close the dialog
    if (!this.langSetSelected.length) {
      return this.props.dismiss()
    }

    // data type required by image update call, could be moved closer to the call
    const languageReferences = this.langSetSelected

    // Get the original image to pass its url
    const { options: { currentLang } } = this.props
    const originalImage = i18n(this.currentImage, 'draft.original', currentLang)

    return (
      this.props.proceed({
        languageReferences,
        url: originalImage.url
      })
    )
  }

  @autobind
  handleCheckboxChange({ target: { checked, name } }) {
    let currentList = this.langSetSelected

    // Select All checkbox, either all on or off
    if (name === 'all') {
      currentList = checked ? this.completeLanguageSet : []
      if (this.disabledSet.size > 0 && checked) {
        // remove disabled entries from currentList
        currentList = currentList.filter(lang => !this.disabledSet.has(lang))
      }

    }

    // single selections
    else {
      if (checked) {
        if (currentList.indexOf(name) === -1) {
          currentList.push(name)
        }
      }
      else {
        currentList.splice(currentList.indexOf(name), 1)
      }
    }

    this.setLangGroupSelect(currentList)

  }

  renderList() {

    const { cachedImage, currentLang, isRemoval } = this.props.options
    const currentImageName = i18n(this.currentImage, 'draft.original.realName', currentLang)

    return (this.completeLanguageSet.map((languageName) => {

      const isCurrent = languageName === currentLang

      const realName = this.currentImage && i18n(this.currentImage, 'draft.original.realName', languageName) || ''

      // disabled if isCurrent or isRemoval and the image doesn't match current
      let disabled = isCurrent
      if (isRemoval && !isCurrent) {
        const match = (currentImageName !== realName)
        disabled = disabled || match
        if (match) {
          this.disabledSet.add(languageName)
        }
      }

      return (<ImagePropagationLanguageEntry
        key={languageName}
        languageName={languageName}
        disabled={disabled}
        checked={this.langSetSelected.indexOf(languageName) !== -1 || isCurrent}
        realName={realName}
        cachedImage={cachedImage}
        currentImage={this.getCurrentImageForLanguage(languageName)}
        onCheck={this.handleCheckboxChange}
        isRemoval={isRemoval}
      />)

    }))
  }

  @autobind
  renderFooter() {
    const { options: { isRemoval } } = this.props

    return (
      <div className="grid-content text-right">
        <GenevaButton
          type="button"
          className="small button float"
          onClick={this.props.dismiss}
        >
          <FormattedMessage id="link-dialog.cancel" />
        </GenevaButton>

        <GenevaButton
          type="submit"
          className="small primary button"
          onClick={this.handleCommit}
          ref={this.handleRef}
        >
          {isRemoval
            ? <FormattedMessage id="link-dialog.delete" />
            : <FormattedMessage id="link-dialog.ok" />
          }
        </GenevaButton>
      </div>
    )
  }

  renderImagePreview(type, image) {
    const { options: { isRemoval } } = this.props

    return (<div
      className={classNames(
        'grid-block shrink',
        css.imagePropagationPreviewImage
      )}
    >
      <img
        src={image && image.src}
        role="presentation"
      />
      { isRemoval
        ? null
        : <div className={classNames(css.imageOverlay, 'align-center')}>
          <FormattedMessage id={`image.image-propagation-${type}`} />
        </div>
      }
    </div>)
  }

  render() {
    const { options: { cachedImage, currentLang, isRemoval } } = this.props

    const newImage = this.getCurrentImageForLanguage()

    // Use different translation keys for removal vs replacement
    const titleKey = isRemoval ? 'image.image-removal-title' : 'image.image-propagation-title'
    const actionKey = isRemoval ? 'image.image-removal-action' : 'image.image-propagation-action'
    const optionsKey = isRemoval ? 'image.image-removal-options' : 'image.image-propagation-options'

    return (
      <Dialog
        className={classNames(
          css.imagePropagation,
          'modal-sectionized'
        )}
        overlayClassName={css.imagePropagation}
        isOpen={this.props.show}
        title={formatMessage({ id: titleKey })}
        onRequestClose={this.handleRequestClose}
        id="imagePropagation"
        renderFooter={this.renderFooter}
      >
        <div className="align-center grid-block vertical">

          <div
            className={classNames(
              css.imagePropagationDescriptionContainer,
              'grid-block',
              'horizontal',
              'modal-section'
            )}
          >

            <div className="grid-block vertical v-align align-center">

              <FormattedMessage id={actionKey} />

              <br />

              <div>
                <FormattedMessage id="image.image-propagation-language" />:
              &nbsp;
                <span className={classNames(css.imagePropagationCurrentLang)}>
                  {currentLang}
                </span>
              </div>
            </div>

            {/* Show current image for removal, or old/new images for replacement */}
            {isRemoval ? this.renderImagePreview('current', newImage) : null}
            {!isRemoval && cachedImage ? this.renderImagePreview('old', cachedImage) : null}
            {!isRemoval ? this.renderImagePreview('new', newImage) : null}

          </div>


          <hr className="modal-section-divider" />


          <div className="grid-block modal-section vertical">

            <div className={css.imagePropagationDescription}>
              <FormattedMessage id={optionsKey} />
            </div>


            <div className="grid-block vertical">

              <div className="grid-block overflow v-align headlines">

                <div className="grid-block medium-4">
                  <input
                    id="all"
                    name="all"
                    onChange={this.handleCheckboxChange}
                    type="checkbox"
                  />
                  <label htmlFor="all">
                    <FormattedMessage id="image.image-propagation-language" />
                  </label>
                </div>

                <div
                  className={classNames(
                    'grid-block medium-5'
                  )}
                >
                  <FormattedMessage id="image.image-propagation-image-title" />
                </div>

                <div
                  className={classNames(
                    'grid-block medium-2 small',
                    css.imagePropagationPreviewImage
                  )}
                >
                  <FormattedMessage id="image.image-propagation-image-preview" />
                </div>

              </div>


              <div className={classNames(css.imagePropagationLanguageList)}>
                {this.renderList()}
              </div>

            </div>
          </div>
        </div>
      </Dialog>
    )
  }
}

export { ImagePropagation as default }
